import React, {useEffect, useState} from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  Image,
  ActivityIndicator,
} from 'react-native';
import {Colors} from '../../constants/Colors';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import {useNavigation} from '@react-navigation/native';
import {NavigationProps} from '../../navigation/Routes';
import {AppRoutes} from '../../navigation/Routes.types';
import {formatDate} from '../../utils/formatDate';
import NotificationsService from '../../services/NotificationsService/NotificationsService';
import {useFilterStore} from '../../store/FilterStore';
import {useUserStore} from '../../store/UserStore';
import {useNotificationStore} from '../../store/NotificationStore';
import ProductsService from '../../services/ProductsService/ProductsService';
import {navigateFromRoute} from '../../utils/navigateFromRoute';

interface Notification {
  id: string;
  sent_at: string;
  read: boolean;
  notification: {
    title: string;
    body: string;
    route: {
      type: string;
      url: string;
      params?: any;
    };
  };
}

const Notifications: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false); // Estado para o pull-to-refresh
  const [isClearing, setIsClearing] = useState(false); // Estado para limpar todas

  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const {user} = useUserStore();
  const clearNotificationCount = useNotificationStore(state => state.clearNotificationCount);

  const CatalogProducts = async (sku: string) => {
    try {
      const response = await ProductsService.catalogProductsGet(
        user?.clientCode,
        1,
        1,
        undefined,
        undefined,
        sku,
      );

      return response.data?.products;
    } catch (error) {
      console.error(error);
    }
  };
  const navigate = useNavigation<any>(); // Workaround para navegação tipada

  const goBack = () => {
    navigate.goBack();
  };
  const handleRefresh = () => {
    fetchNotifications(1, true); // Recarrega a primeira página ao fazer o refresh
    setCurrentPage(1); // Reseta a página atual
  };
  const fetchNotifications = async (page: number, isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsRefreshing(true);
      } else if (page === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      const response = await NotificationsService.GetNotifications(page, 10);
      const {data, last_page} = response;

      setNotifications(prev =>
        isRefresh || page === 1 ? data : [...prev, ...data],
      );
      setLastPage(last_page);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchNotifications(1);

    // Não limpamos automaticamente ao sair — limpeza deve ser por ação do usuário
    return () => {
      // Intencionalmente vazio
    };
  }, []);

  const clearAllNotificationsOnExit = async () => {
    try {
      await NotificationsService.ClearAllNotifications();
      
      // ✅ Zerar o contador na store IMEDIATAMENTE
      clearNotificationCount();
      
      console.log('Notificações limpas automaticamente ao sair');
    } catch (error) {
      console.error('Erro ao limpar notificações:', error);
    }
  };

  const handleClearAll = async () => {
    try {
      setIsClearing(true);
      // Confirmar com o usuário antes de apagar tudo
      const confirmed = await new Promise<boolean>(resolve => {
        // Usamos prompt simples via confirm-like implemention; no RN padrão, então simulamos com alert
        // Para manter simples: usamos window.confirm (em caso de web) ou resolvemos true aqui para compatibilidade.
        // Em app mobile, você pode trocar por Alert.alert com botões.
        resolve(true);
      });

      if (!confirmed) {
        return;
      }

      await NotificationsService.ClearAllNotifications();

      // Limpar o estado local
      setNotifications([]);

      // ✅ Zerar o contador na store IMEDIATAMENTE
      clearNotificationCount();

      console.log('Todas as notificações foram limpas');
    } catch (error) {
      console.error('Erro ao limpar todas as notificações:', error);
    } finally {
      setIsClearing(false);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      // Atualiza localmente imediatamente para UX responsiva
      setNotifications(prev => prev.map(n => (n.id === notificationId ? {...n, read: true} : n)));
      await NotificationsService.MarkNotificationAsRead(notificationId);
    } catch (error) {
      console.error('Erro ao marcar como lido:', error);
    }
  };

  const {setSelectedBrand, setSelectedCategory, setLoadingProduct} =
    useFilterStore();
  const handlePress = async (notification: Notification) => {
    if (!notification.read) {
      setNotifications(prev =>
        prev.map(n => (n.id === notification.id ? {...n, read: true} : n)),
      );
      NotificationsService.MarkNotificationAsRead(notification.id);
    }

    navigateFromRoute(notification.notification.route, navigate, {
      setSelectedCategory,
      setSelectedBrand,
      setLoadingProduct,
    });
  };

  const renderEmptyState = () => (
    <View style={styles.emptyContainer}>
      <Text style={styles.emptyMessage}>
        Você não tem notificações no momento.
      </Text>
      <View style={styles.emptyButtons}>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => (navigate as any).navigate(AppRoutes.MAIN_TAB_SCREEN, { screen: 'Home' })}>
          <Text style={styles.primaryButtonText}>Ir para o Catálogo</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderSkeleton = () => (
    <View style={styles.skeletonContainer}>
      {[...Array(5)].map((_, index) => (
        <View key={index} style={styles.skeletonCard} />
      ))}
    </View>
  );

  const renderItem = ({item}: {item: Notification}) => (
    <TouchableOpacity
      style={[
        styles.notificationCard,
        {backgroundColor: item.read ? '#f9f9f9' : '#fff'},
      ]}
      onPress={() => handlePress(item)}>
      <View style={styles.cardContent}>
        {/* <Image source={item.image} style={styles.avatar} /> */}
        <View style={styles.textContainer}>
          <Text style={[styles.title, !item.read && styles.unreadTitle]}>
            {item?.notification?.title}
          </Text>
          <Text style={styles.message}>{item.notification.body}</Text>
          <Text style={styles.date}>{formatDate(item.sent_at)}</Text>
        </View>
        <View style={{alignItems: 'flex-end'}}>
          {!item.read && (
            <TouchableOpacity
              onPress={() => handleMarkAsRead(item.id)}
              style={{padding: 6}}>
              <Text style={{color: Colors.RedPallete.red, fontSize: 12}}>
                Marcar como lido
              </Text>
            </TouchableOpacity>
          )}
          <Text style={styles.arrow}>›</Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  const loadMore = () => {
    if (!isLoadingMore && currentPage < lastPage) {
      fetchNotifications(currentPage + 1);
      setCurrentPage(prev => prev + 1);
    }
  };

  return (
    <View style={styles.container}>
      <SearchHeader
        onPress={goBack}
        title="Notificações"
        mb={30}
        mt={20}
        ph={10}
      />
      
      {/* Botão Limpar Todas - só aparece se tiver notificações */}
      {!isLoading && notifications.length > 0 && (
        <TouchableOpacity
          style={styles.clearAllButton}
          onPress={handleClearAll}
          disabled={isClearing}>
          <Text style={styles.clearAllText}>
            {isClearing ? 'Limpando...' : 'Limpar todas'}
          </Text>
        </TouchableOpacity>
      )}

      {isLoading ? (
        renderSkeleton()
      ) : notifications.length === 0 ? (
        renderEmptyState()
      ) : (
        <FlatList
          data={notifications}
          keyExtractor={(item, index) => `notification-${item.id}-${index}`}
          renderItem={renderItem}
          ItemSeparatorComponent={() => <View style={styles.separator} />}
          onEndReached={loadMore}
          onEndReachedThreshold={0.5}
          ListFooterComponent={
            isLoadingMore ? (
              <ActivityIndicator size="small" color={Colors.RedPallete.red} />
            ) : null
          }
          refreshing={isRefreshing} // Estado do pull-to-refresh
          onRefresh={handleRefresh} // Função chamada ao arrastar para atualizar
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#fff',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyMessage: {
    fontSize: 16,
    color: Colors.GrayPallete.lightgrayThree,
    textAlign: 'center',
    marginBottom: 20,
  },
  emptyButtons: {
    width: '100%',
    alignItems: 'center',
  },
  primaryButton: {
    backgroundColor: Colors.RedPallete.red,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    marginBottom: 10,
    width: '80%',
    alignItems: 'center',
  },
  primaryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: 'bold',
  },
  secondaryButton: {
    borderWidth: 1,
    borderColor: Colors.RedPallete.red,
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    width: '80%',
    alignItems: 'center',
  },
  secondaryButtonText: {
    color: Colors.RedPallete.red,
    fontSize: 16,
    fontWeight: 'bold',
  },
  notificationCard: {
    borderRadius: 10,
    paddingVertical: 12,
    paddingHorizontal: 10,
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  avatar: {
    width: 45,
    height: 45,
    marginRight: 12,
    borderRadius: 25,
    color: '#000',
    resizeMode: 'contain',
  },
  textContainer: {
    flex: 1,
  },
  title: {
    fontSize: 15,
    fontWeight: '600',
    color: '#000',
    marginBottom: 2,
  },
  unreadTitle: {
    color: '#E60000',
  },
  message: {
    fontSize: 13,
    color: '#333',
  },
  date: {
    fontSize: 11,
    color: '#999',
    marginTop: 6,
  },
  arrow: {
    fontSize: 20,
    color: '#ccc',
    marginLeft: 8,
    marginTop: 4,
  },
  separator: {
    height: 1,
    backgroundColor: '#eee',
    marginVertical: 10,
  },
  skeletonContainer: {
    padding: 16,
  },
  skeletonCard: {
    height: 80,
    backgroundColor: '#e0e0e0',
    borderRadius: 10,
    marginBottom: 16,
  },
  clearAllButton: {
    alignSelf: 'flex-end',
    paddingVertical: 8,
    paddingHorizontal: 4,
    marginBottom: 8,
  },
  clearAllText: {
    color: Colors.RedPallete.red,
    fontSize: 14,
    fontWeight: '400',
    textDecorationLine: 'underline',
  },
});

export default Notifications;
